import React, { useMemo } from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { PullRequestDetails } from '@finvari/deliver';
import { TEST_LANES } from '../../../workflow/workflowConfig';
import { laneRequired } from '../../../workflow/prWorkflow';

interface FilesTabProps {
  pr: PullRequestDetails;
}

// Which scoped-test lanes a single file triggers (drives the per-file chips).
function lanesForFile(file: string): string[] {
  return TEST_LANES.filter(lane => laneRequired(lane, [file])).map(lane => lane.label);
}

const FilesTab: React.FC<FilesTabProps> = ({ pr }) => {
  const changedFiles = useMemo(() => pr.changedFiles ?? [], [pr.changedFiles]);

  const grouped = useMemo(() => {
    const byDir = new Map<string, string[]>();
    for (const file of changedFiles) {
      const slash = file.indexOf('/');
      const top = slash === -1 ? '(root)' : file.slice(0, slash);
      const list = byDir.get(top);
      if (list) list.push(file);
      else byDir.set(top, [file]);
    }
    return Array.from(byDir.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [changedFiles]);

  if (changedFiles.length === 0) {
    return <Typography variant="body2" color="text.secondary">No changed files recorded for this PR.</Typography>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">{changedFiles.length} changed files</Typography>
      {grouped.map(([dir, files]) => (
        <Box key={dir}>
          <Typography variant="subtitle2" gutterBottom>{dir}/ <Typography component="span" variant="caption" color="text.secondary">({files.length})</Typography></Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {files.map(file => {
              const lanes = lanesForFile(file);
              return (
                <Box key={file} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>{file}</Typography>
                  {lanes.map(label => (
                    <Tooltip key={label} title={`Triggers ${label}`}>
                      <Chip size="small" color="info" variant="outlined" label={label} sx={{ height: 18, fontSize: 10 }} />
                    </Tooltip>
                  ))}
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default FilesTab;
